import type {
  GemMaterialParams,
  GemUniformName,
  MetalMaterialParams,
  MetalParamName,
  PostFXSettings,
  PostFXSettingsPatch,
  RingClientApi,
  SceneState,
  Vec3,
} from "../../../shared/api-contract";
import type { Binding } from "./schema";

/**
 * PostFX lens: read a value out of PostFXSettings, write a nested
 * PostFXSettingsPatch. `setPostFX` returns the full effective settings, so the
 * reconcile simply replaces the slice — no field-by-field merge needed.
 */
export function fx<T>(
  read: (s: PostFXSettings) => T,
  write: (v: T) => PostFXSettingsPatch,
): Binding<T> {
  return {
    domain: "postFX",
    get: (r) => read(r("postFX") as PostFXSettings),
    set: (v) => {
      const patch = write(v);
      return { patch, call: (api) => api.setPostFX(patch), reconcile: (res) => res };
    },
  };
}

/**
 * SceneState scalar field with a dedicated void setter. Optimistic patch keeps
 * the slice coherent; there's no return value to reconcile.
 */
export function sc<T>(
  read: (s: SceneState) => T,
  patch: (v: T) => Partial<SceneState>,
  rpc: (api: RingClientApi, v: T) => Promise<unknown> | void,
): Binding<T> {
  return {
    domain: "scene",
    get: (r) => read(r("scene") as SceneState),
    set: (v) => ({ patch: patch(v), call: (api) => Promise.resolve(rpc(api, v)) }),
  };
}

/**
 * SceneState sub-object backed by a setter that RETURNS the canonical
 * sub-object (e.g. `setGroundShadowBake(patch) → GroundShadowBakeSettings`).
 * Reads a scalar out of the sub-object; writes a one-key patch of it, then
 * reconciles the RPC's returned sub-object back into the scene slice.
 */
export function scReturning<T, S>(
  subKey: keyof SceneState,
  read: (sub: S) => T,
  field: string,
  rpc: (api: RingClientApi, patch: Record<string, T>) => Promise<S>,
  extraPatch?: (v: T) => Partial<SceneState>,
): Binding<T> {
  return {
    domain: "scene",
    get: (r) => read((r("scene") as SceneState)[subKey] as S),
    set: (v) => {
      const patchArg = { [field]: v } as Record<string, T>;
      return {
        patch: { [subKey]: patchArg, ...(extraPatch ? extraPatch(v) : {}) } as Partial<SceneState>,
        call: (api) => rpc(api, patchArg),
        reconcile: (res, prev) => ({ ...(prev as SceneState), [subKey]: res }) as SceneState,
      };
    },
  };
}

/** SceneState Vec3 field exposed to a vec3 row as [x, y, z]. */
export function scVec3(
  read: (s: SceneState) => Vec3,
  patchKey: keyof SceneState,
  rpc: (api: RingClientApi, x: number, y: number, z: number) => Promise<unknown> | void,
): Binding<[number, number, number]> {
  return {
    domain: "scene",
    get: (r) => {
      const v = read(r("scene") as SceneState);
      return [v.x, v.y, v.z];
    },
    set: ([x, y, z]) => ({
      patch: { [patchKey]: { x, y, z } } as Partial<SceneState>,
      call: (api) => Promise.resolve(rpc(api, x, y, z)),
    }),
  };
}

/** PostFX Vec3 lens as an [x,y,z] tuple (for vec3 rows like SSR boost). */
export function fxVec3(
  read: (s: PostFXSettings) => Vec3,
  write: (v: Vec3) => PostFXSettingsPatch,
): Binding<[number, number, number]> {
  return {
    domain: "postFX",
    get: (r) => {
      const v = read(r("postFX") as PostFXSettings);
      return [v.x, v.y, v.z];
    },
    set: ([x, y, z]) => {
      const patch = write({ x, y, z });
      return { patch, call: (api) => api.setPostFX(patch), reconcile: (res) => res };
    },
  };
}

// ── Material (selected group) bindings ─────────────────────────────────────

export function matMetalParam(name: MetalParamName): Binding<number> {
  return {
    domain: "material",
    get: (r) => {
      const m = r("material") as MetalMaterialParams | null;
      return m && m.kind === "metal" ? (m[name] as number) : 0;
    },
    set: (v, ctx) => {
      const id = ctx.read("selection") as string | null;
      const mat = ctx.read("material");
      return {
        patch: mat ? { [name]: v } : undefined,
        call: id ? (api) => Promise.resolve(api.setGroupMetalParam(id, name, v)) : undefined,
      };
    },
  };
}

export function matMetalColor(): Binding<string> {
  return {
    domain: "material",
    get: (r) => {
      const m = r("material") as MetalMaterialParams | null;
      return m && m.kind === "metal" ? m.color : "#cccccc";
    },
    set: (hex, ctx) => {
      const id = ctx.read("selection") as string | null;
      const mat = ctx.read("material");
      return {
        patch: mat ? { color: hex } : undefined,
        call: id ? (api) => Promise.resolve(api.setGroupMetalColor(id, hex)) : undefined,
      };
    },
  };
}

export function matGemUniform(name: GemUniformName): Binding<number> {
  return {
    domain: "material",
    get: (r) => {
      const m = r("material") as GemMaterialParams | null;
      return m && m.kind === "gem" ? (m[name] as number) : 0;
    },
    set: (v, ctx) => {
      const id = ctx.read("selection") as string | null;
      const mat = ctx.read("material");
      return {
        patch: mat ? { [name]: v } : undefined,
        call: id ? (api) => Promise.resolve(api.setGroupGemUniform(id, name, v)) : undefined,
      };
    },
  };
}

const VEC_IDX = { x: 0, y: 1, z: 2 } as const;

export function matGemVec3(field: "boostFactors" | "color", comp: "x" | "y" | "z"): Binding<number> {
  return {
    domain: "material",
    get: (r) => {
      const m = r("material") as GemMaterialParams | null;
      return m && m.kind === "gem" ? m[field][VEC_IDX[comp]] : 0;
    },
    set: (v, ctx) => {
      const id = ctx.read("selection") as string | null;
      const mat = ctx.read("material") as GemMaterialParams | null;
      // Patch the whole vector with this component replaced. Without it the
      // slice never changes, so this fully-controlled slider re-renders at its
      // OLD value the moment anything else notifies — it reads as a dead control
      // even though the viewer did apply the edit.
      let patch: Record<string, unknown> | undefined;
      if (mat && mat.kind === "gem") {
        const next = [...mat[field]] as [number, number, number];
        next[VEC_IDX[comp]] = v;
        patch = { [field]: next };
      }
      return {
        patch,
        call: id ? (api) => Promise.resolve(api.setGroupGemVec3Component(id, field, comp, v)) : undefined,
      };
    },
  };
}

/** Gem body color as a hex picker (maps the 0..1 channels of the gem `color`
 * uniform; the fine 0..2 R/G/B sliders remain for HDR boosts). */
export function matGemColorHex(): Binding<string | null> {
  const to = (x: number) => Math.round(Math.min(1, Math.max(0, x)) * 255).toString(16).padStart(2, "0");
  return {
    domain: "material",
    get: (r) => {
      const m = r("material") as GemMaterialParams | null;
      if (!m || m.kind !== "gem") return "#ffffff";
      const c = m.color;
      return "#" + to(c[0]) + to(c[1]) + to(c[2]);
    },
    set: (hex, ctx) => {
      const id = ctx.read("selection") as string | null;
      const h = (hex ?? "#ffffff").replace("#", "");
      const ch = (i: number) => parseInt(h.slice(i, i + 2), 16) / 255;
      const r = ch(0), g = ch(2), b = ch(4);
      const mat = ctx.read("material") as GemMaterialParams | null;
      return {
        // CompactColor is fully controlled off `get`, so with no patch the
        // swatch snapped straight back to the previous colour on the next
        // notify while the stone had in fact changed.
        patch: mat && mat.kind === "gem" ? { color: [r, g, b] as [number, number, number] } : undefined,
        call: id
          ? async (api) => {
              await api.setGroupGemVec3Component(id, "color", "x", r);
              await api.setGroupGemVec3Component(id, "color", "y", g);
              await api.setGroupGemVec3Component(id, "color", "z", b);
            }
          : undefined,
      };
    },
  };
}

export function matGemOpacity(): Binding<number> {
  return {
    domain: "material",
    get: (r) => {
      const m = r("material") as GemMaterialParams | null;
      return m && m.kind === "gem" ? m.opacity : 1;
    },
    set: (v, ctx) => {
      const id = ctx.read("selection") as string | null;
      return {
        patch: { opacity: v },
        call: id ? (api) => Promise.resolve(api.setGroupGemOpacity(id, v)) : undefined,
      };
    },
  };
}

/** Pure local-UI binding backed by the `ui` slice (no RPC). */
export function uiBinding<T>(
  read: (ui: Record<string, unknown>) => T,
  write: (v: T, ui: Record<string, unknown>) => Record<string, unknown>,
): Binding<T> {
  return {
    domain: "ui",
    get: (r) => read(r("ui") as Record<string, unknown>),
    set: (v, ctx) => ({ patch: write(v, ctx.read("ui") as Record<string, unknown>) }),
  };
}
